// @TODO: this container on web should have query params for selected book, translation, and chapter number for url manipulation
// @TODO: on small enough screens on web, prev and next could be inaccessible...should probably be visible in addition to the click/drag gesture
/**
 * Ryan O'Dowd
 * 2018-02-05
 * © Copyright 2018 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import Animated, {
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  Gesture,
  GestureDetector,
} from 'react-native-gesture-handler';
import {
  Platform,
  TouchableOpacity,
  View,
} from 'react-native';
import React, {
  useEffect,
  useState,
} from 'react';
import {
  addToReadingHistory,
  setPreferredScale,
  setReadingBookmarkChapterId,
  updateUserSettings,
} from '@utils/store/actions';
import BackgroundGradient from '@components/common/BackgroundGradient';
import BookAndTranslationSelection from './BookAndTranslationSelection';
import Button from '@components/common/Button';
import CPassage from '@classes/Passage';
import Dialog from '@components/common/Dialog';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import Icon from '@react-native-vector-icons/material-design-icons';
import Menu from '@components/common/Menu';
import PassageDisplay from '@components/PassageDisplay';
import PassageSelection from '@components/PassageSelection';
import ReadingHistoryDialog from '@components/ReadingHistoryDialog';
import ScreenWrapper from '@containers/ScreenWrapper';
/* @TODO: use this to scrub to a verse...it's just a slider until you scrub, then there's a tooltip with the verse number...maybe it's horizontal
import Slider from '@react-native-community/slider';
*/
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

// @TODO: perf: if the preferred translation is esv (for example), and a read button for a non-esv translation is clicked, the app fetches esv unnecessarily on initially render
const Read = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const locationState = hooks.useLocationState();
  const styles = hooks.useStyles(getStyles);

  const readingBookmarkChapterId = hooks.useTypedSelector((state) => state.readingBookmarkChapterId);
  const [_selectedChapterId, setSelectedChapterId] = useState(locationState.chapterId ?? readingBookmarkChapterId);
  const [_isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [_isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [_isScaleMenuOpen, setIsScaleMenuOpen] = useState(false);
  const [_isPassageSelectionOpen, setIsPassageSelectionOpen] = useState(false);

  const preferredTranslation = hooks.usePreferredTranslation();
  useEffect(() => {
    if (locationState.contextPassage?.translationId && (locationState.contextPassage.translationId !== preferredTranslation.id)) {
      dispatch(updateUserSettings({preferred_translation_id: locationState.contextPassage.translationId}));
    }

    if (locationState.contextPassage?.translationId || locationState.contextPassage?.translation_id) {
      // web requires removing translation id from contextPassage in the url or else it won't allow manually modification of it
      navigation.setParams({contextPassage: undefined});
      const updatedParams = {};
      for (const [key, value] of Object.entries(locationState)) {
        if (key === 'contextPassage') {
          const updatedContextPassage = {};
          for (const [contextKey, contextValue] of Object.entries(value)) {
            if (['translationId', 'translation_id'].includes(contextKey)) {
              continue;
            }
            updatedContextPassage[contextKey] = contextValue;
          }
          updatedParams[key] = JSON.stringify(updatedContextPassage);
        } else {
          updatedParams[key] = value;
        }
      }
      navigation.setParams(updatedParams);
    }
  }, [dispatch, preferredTranslation.id, locationState, navigation]);

  const verticalScrollRef: any = React.createRef();

  const allChapters = hooks.useTypedSelector((state) => state.chapters);
  const selectedChapter = hooks.useTypedSelector((state) => state.chapters.find((chapter) => chapter.id === _selectedChapterId));
  const previousChapter = hooks.useTypedSelector((state) => state.chapters.find((chapter) => chapter.id === _selectedChapterId - 1));
  const nextChapter = hooks.useTypedSelector((state) => state.chapters.find((chapter) => chapter.id === _selectedChapterId + 1));

  useEffect(() => {
    if (locationState.chapterId) {
      dispatch(addToReadingHistory(locationState.chapterId));
    }
  }, [locationState.chapterId, dispatch]);

  const backIcon = (
    <Header.BackIcon
      onPress={() => {
        navigation.goBack();
        dispatch(addToReadingHistory(_selectedChapterId));
      }}
    />
  );

  /* @TODO:
  const searchIcon = (
    <Header.Icon
      icon='magnify'
      onPress={() => {
        const newSearchOpenState = !_searchOpen;
        if (!newSearchOpenState) {
          setSearchText('');
        }
        setSearchOpen(newSearchOpenState);
      }}
    />
  );
  */

  const historyIcon = (
    <Header.Icon
      icon='history'
      onPress={() => setIsHistoryDialogOpen(true)}
    />
  );

  const helpIcon = (
    <Header.Icon
      icon='help-circle'
      onPress={() => setIsHelpDialogOpen(true)}
    />
  );

  const settingsIcon = (
    <Menu
      statusBarHeight={styles.safeAreaInsets.top}// @TODO: abstract menu to oak component and abstract this prop away
      anchor={
        <Header.Icon
          icon='format-size'
          onPress={() => setIsScaleMenuOpen(true)}
        />
      }
      visible={_isScaleMenuOpen}
      onDismiss={() => setIsScaleMenuOpen(false)}
    >
      {['small', 'medium', 'large', 'extraLarge'].map((size) => { // @TODO: don't hardcode this
        return (
          <Menu.Item
            key={size}
            title={size === 'extraLarge' ? 'Extra Large' : utilities.capitalizeFirstLetter(size)}
            titleStyle={globals.fontScales[size] === styles.globalScale && styles.selectedMenuItem}// @TODO: convert this to a `selected` prop in a custom (wrapper) component for menu items
            onPress={() => {
              dispatch(setPreferredScale(size));
              setIsScaleMenuOpen(false);
            }}
          />
        );
      })}
    </Menu>
  );

  const passage = new CPassage(
    -1,
    preferredTranslation.id,
    selectedChapter.book_id,
    selectedChapter.chapter_number,
    1,
    selectedChapter.number_of_verses,
  );

  hooks.useBackButton(() => {
    dispatch(addToReadingHistory(_selectedChapterId));
    return false;
  });

  // @TODO: const user = hooks.useTypedSelector((state) => state.oakUser);

  const bookAndChapter = `${CPassage.getBookName(preferredTranslation.id, selectedChapter.book_id)} ${selectedChapter.chapter_number}`;
  // @TODO: if entering via Labels screen with a label that's applied to zero people, empty screen text is "create your first person"

  const THRESHOLD = 50; // @TODO: good value?
  const position = useSharedValue(0);

  // @TODO: abstract these with each other {
  const goToPreviousChapter = () => {
    navigation.setParams({contextPassage: undefined}); // @TODO: this doesn't work on web
    const previousChapterId = Math.max(_selectedChapterId - 1, 1); // @TODO: don't hardcode Genesis
    setSelectedChapterId(previousChapterId);
    dispatch(setReadingBookmarkChapterId(previousChapterId));
    verticalScrollRef.current?.scrollTo({
      y: 0,
      animated: false,
    });
  };
  const goToNextChapter = () => {
    navigation.setParams({contextPassage: undefined}); // @TODO: this doesn't work on web
    const nextChapterId = Math.min(_selectedChapterId + 1, 1189); // @TODO: don't hardcode Revelation
    setSelectedChapterId(nextChapterId);
    dispatch(setReadingBookmarkChapterId(nextChapterId));
    verticalScrollRef.current?.scrollTo({
      y: 0,
      animated: false,
    });
  };
  // @TODO: abstract these with each other }

  const panGesture = Gesture.Pan() // @TODO: good values?
    .onUpdate((e) => {
      if (e.translationX > 0) {
        if (selectedChapter.id === 1) {
          // this is Genesis 1
          return;
        }
        position.value = Math.min(e.translationX, THRESHOLD);
      } else {
        if (selectedChapter.id === 1189) {
          // this is Revelation 22
          return;
        }
        position.value = Math.max(e.translationX, -1 * THRESHOLD);
      }
    })
    .onEnd(() => {
      position.value = withTiming(0, {duration: 100});

      if (Math.abs(position.value) >= THRESHOLD) {
        if (position.value > 0) {
          runOnJS(goToPreviousChapter)();
        } else {
          runOnJS(goToNextChapter)();
        }
      }
    });

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{translateX: position.value}],
  }), [position]);

  const turnPageGradientColor = styles.isDarkMode ? styles.colors.primaryDark : styles.colors.primary;

  const rightItems = [/* @TODO: searchIcon, */historyIcon, settingsIcon]; // @TODO: make sure long names look okay on small devices (deuteronomy 3 for example) (even Genesis 2 on iphone se is too long...put options in overflow menu)

  return (
    <ScreenWrapper>
      <Header
        centerComponent={
          <BookAndTranslationSelection
            onPress={() => setIsPassageSelectionOpen(true)}
            bookAndChapter={bookAndChapter}
            selectedTranslation={preferredTranslation}
            selectedChapter={selectedChapter}
          />
        }
        alwaysCenterTitle={true}
        leftItems={[backIcon, helpIcon]}
        rightItems={rightItems}
        isMultilineAllowed={true}
      />
      {/* @TODO: would be cool if text faded out as it scrolled behind header...small thing but cool ui */}
      <GestureDetector
        gesture={panGesture}
        // @TODO: is this deprecated? waitFor={verticalScrollRef}
      >
        <Animated.View style={[styles.innerContainer, animatedStyle]}>
          <View style={styles.innerWrapper}>
            {!_isPassageSelectionOpen && previousChapter &&
              <TouchableOpacity
                style={styles.prev}
                onPress={goToPreviousChapter}
              >
                <BackgroundGradient
                  colors={[`${turnPageGradientColor}ff`, `${turnPageGradientColor}00`]}
                  linearGradientProps={{
                    useAngle: true,
                    angle: -45,
                    angleCenter: {x: 1, y: 0},
                  }}
                />
                <Icon
                  style={[styles.pageIcon, styles.pageIconPrev]}
                  name='arrow-left'
                />
                <View style={styles.labelWrapper}>
                  <Text style={styles.chapterLabel}>
                    {`${CPassage.getBookName(preferredTranslation.id, previousChapter.book_id)} ${previousChapter.chapter_number}`}
                  </Text>
                </View>
              </TouchableOpacity>
            }
            <FullScreenScrollView noScroll={Platform.OS !== 'web'}>{/* @TODO: selection menu on web should be sticky like on mobile (funny, but the noScroll prop doesn't seem to be the thing preventing this on web) */}
              <PassageDisplay// @TODO: should be full height minimum
                key={selectedChapter.id}
                passages={[passage]}// @TODO: not sure where this should live
                scrollRef={verticalScrollRef}
                isPressDisabled={false}// @TODO: this needs to be true when we're panning, but i can find a way to listen to pan status
                showFormatting={true}
                showFootnotes={true}
                showHeadings={true}
                showContextHighlight={true}// @TODO: why is this an error, but the other props are not?
                hideNumbersTextToggle={true}
                hideReadInContext={true}
                showToggleBadges={true}
                spritzPassage={passage}
                spritzGoToPreviousPassage={!previousChapter ? null : () => goToPreviousChapter()}
                spritzGoToNextPassage={!nextChapter ? null : () => goToNextChapter()}
                isUnattachedToGoal={true}
                showWrinkleBadges={false}
                alwaysShowSelectionMenu={true}// @TODO: would prefer this not to be true...but there's jumping when selecting otherwise
                mainListContainerStyle={styles.verseWrapperContainer}
                mainListStyle={styles.verseWrapperContainerOuter}
                passageForContext={locationState.contextPassage}
              />
            </FullScreenScrollView>
            {!_isPassageSelectionOpen && nextChapter &&
              <TouchableOpacity
                style={styles.next}
                onPress={goToNextChapter}
              >
                <BackgroundGradient
                  colors={[`${turnPageGradientColor}88`, `${turnPageGradientColor}00`]}
                  linearGradientProps={{
                    useAngle: true,
                    angle: 45,
                    angleCenter: {x: 1, y: 0},
                  }}
                />
                <Icon
                  style={[styles.pageIcon, styles.pageIconNext]}
                  name='arrow-right'
                />
                <View style={styles.labelWrapper}>
                  <Text style={styles.chapterLabel}>
                    {`${CPassage.getBookName(preferredTranslation.id, nextChapter.book_id)} ${nextChapter.chapter_number}`}
                  </Text>
                </View>
              </TouchableOpacity>
            }
          </View>
        </Animated.View>
      </GestureDetector>
      {_isHistoryDialogOpen && (
        <ReadingHistoryDialog
          onClose={() => setIsHistoryDialogOpen(false)}
          onSelect={(chapterId) => {
            setSelectedChapterId(chapterId);
            setIsHistoryDialogOpen(false);
          }}
        />
      )}
      {_isHelpDialogOpen &&
        <Dialog
          visible={_isHelpDialogOpen}
          onDismiss={() => setIsHelpDialogOpen(false)}
        >{/* @TODO: abstract this modal to it's own component and make available in Settings component as well */}
          {/* @TODO: this dialog doesn't fit on some screen sizes (definitely not if a11y is set to extra large) */}
          <Dialog.Title>{i18n('readHelpTitle')}</Dialog.Title>
          <Dialog.Content>
            <View style={styles.sectionWrapper}>
              <Text style={styles.dialogText}>{i18n('readHelpDescription')}</Text>
            </View>
            <View style={styles.legendWrapper}>
              {/* @TODO:
              <View style={[styles.verseWrapper, styles.demoVerseWrapper]}>
                <Text style={styles.typedVerse}>
                  Verses that aren't attached to any goal will show up like this.
                </Text>
              </View>
              */}
              {/* @TODO: abstract to use Verses component */}
              <View style={[styles.verseWrapper, styles.demoVerseWrapper, styles.unstartedVerseWrapper]}>
                <Text style={[styles.typedVerse, styles.unstartedVerseText]}>
                  {i18n('readHelpUnstarted')}
                </Text>
              </View>
              <View style={[styles.verseWrapper, styles.demoVerseWrapper, styles.inProgressVerseWrapper]}>
                <Text style={[styles.typedVerse, styles.inProgressVerseText]}>
                  {i18n('readHelpInProgress')}
                </Text>
              </View>
              <View style={[styles.verseWrapper, styles.demoVerseWrapper, styles.completedVerseWrapper]}>
                <Text style={[styles.typedVerse, styles.completedVerseText]}>
                  {i18n('readHelpCompleted')}
                </Text>
              </View>
              <View style={[styles.verseWrapper, styles.demoVerseWrapper, styles.highlightedVerseWrapper]}>
                <Text style={[styles.typedVerse, styles.highlightedVerseText]}>
                  {i18n('versesHighlightedForReadingInContext')}
                </Text>
              </View>
              <View style={[styles.verseWrapper, styles.demoVerseWrapper, styles.selectedVerseWrapper]}>
                <Text style={[styles.typedVerse, styles.selectedVerseText]}>
                  {i18n('readHelpSelected')}
                </Text>
              </View>
            </View>
          </Dialog.Content>
          <Dialog.Actions>
            <Button
              mode='contained'
              onPress={() => setIsHelpDialogOpen(false)}
            >
              {i18n('close')}
            </Button>
          </Dialog.Actions>
        </Dialog>
      }
      {_isPassageSelectionOpen &&
        <PassageSelection
          onClose={() => setIsPassageSelectionOpen(false)}
          onSelectPassage={(newPassageJson) => {
            const chapterId = allChapters.find((c) => c.book_id === newPassageJson.bookId && c.chapter_number === newPassageJson.chapterNumber).id;
            setSelectedChapterId(chapterId);
            dispatch(addToReadingHistory(chapterId));
            dispatch(setReadingBookmarkChapterId(chapterId));
            setIsPassageSelectionOpen(false);
          }}
          chapterSelectionOnly={true}
          selectedChapter={selectedChapter}
        />
      }
    </ScreenWrapper>
  );
};

export default Read;
